import { DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  signal,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { SiteState } from '@next/core-lib/site';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { UserSelectors } from '@next/core-lib/user';
import { filter, merge, take } from 'rxjs';
import { selectCropYear } from '../../../store/selectors';
import { loadSoilTypesInitiate, SoilTypeSelectors } from '../../../soil-type';
import { PriorityRankingComponent } from './priority-ranking/priority-ranking.component';
import { QuestionnaireStepsComponent } from './questionnaire-steps/questionnaire-steps.component';
import { GreenManureCropsService } from './green-manure-crops.service';
import { GreenManureQuestionnaireService } from './green-manure-questionnaire.service';
import {
  DISEASES,
  ConcernOption,
  CropFieldSnapshot,
  DiseaseId,
  ECOSYSTEM_PRIORITY_LIMIT,
  GreenManureData,
  MANAGEMENT_SYSTEMS,
  NEMATODES,
  NematodeId,
  PRIORITY_LABEL_KEYS,
} from './green-manure.model';

/** Edits the questionnaire draft; advice calculation remains separate. */
@Component({
  selector: 'app-green-manure',
  providers: [GreenManureCropsService, GreenManureQuestionnaireService],
  imports: [
    DatePipe,
    DecimalPipe,
    NgTemplateOutlet,
    MatButtonModule,
    MatCheckbox,
    MatDialogModule,
    MatIcon,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatTooltipModule,
    PriorityRankingComponent,
    QuestionnaireStepsComponent,
    TranslatePipe,
  ],
  templateUrl: './green-manure.component.html',
  styleUrl: './green-manure.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GreenManureComponent {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly soilTypes = this.store.selectSignal(SoilTypeSelectors.selectData);
  private readonly soilTypesLoaded = this.store.selectSignal(SoilTypeSelectors.selectIsLoaded);
  readonly soilTypesFailed = this.store.selectSignal(SoilTypeSelectors.selectIsError);
  private readonly currentCustomer = this.store.selectSignal(UserSelectors.selectCustomer);
  private readonly currentCropYear = this.store.selectSignal(selectCropYear);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogs = inject(DialogService);
  private readonly localeService = inject(LocaleService);
  private readonly snackBar = inject(MatSnackBar);
  private confirming = false;
  readonly questionnaire = inject(GreenManureQuestionnaireService);
  readonly cropOptions = inject(GreenManureCropsService);
  readonly mainCrops = computed(() => {
    const state = this.cropOptions.state();
    return state.status === 'loaded' ? state.crops : [];
  });
  readonly selectedMainCropId = this.questionnaire.selectedMainCropId;
  readonly selectedMainCrop = computed(
    () => this.mainCrops().find((entry) => entry.id === this.selectedMainCropId()) ?? null,
  );
  readonly isNonInversionTillage = this.questionnaire.isNonInversionTillage;
  readonly data = inject<GreenManureData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject<MatDialogRef<GreenManureComponent>>(MatDialogRef);
  readonly locale = inject(LocaleService).getLocale();
  readonly context = {
    customerId: this.data.customerId,
    cropFieldId: this.data.field.cropField.id,
    cropYear: this.data.cropYear,
  };
  readonly contextValid = computed(
    () => this.currentCustomer()?.id === this.context.customerId && this.currentCropYear() === this.context.cropYear,
  );
  readonly nematodes: readonly ConcernOption<NematodeId>[] = NEMATODES;
  readonly diseases: readonly ConcernOption<DiseaseId>[] = DISEASES;
  readonly systems = MANAGEMENT_SYSTEMS;
  readonly selectedPriorities = this.questionnaire.selectedPriorities;
  readonly priorityLabelKeys = PRIORITY_LABEL_KEYS;
  readonly prioritiesComplete = computed(
    () =>
      this.selectedPriorities().length === ECOSYSTEM_PRIORITY_LIMIT &&
      new Set(this.selectedPriorities()).size === ECOSYSTEM_PRIORITY_LIMIT,
  );
  readonly rankedPriorities = computed(() => this.selectedPriorities().map((id, index) => ({ id, rank: index + 1 })));
  readonly selectedNematodes = this.questionnaire.selectedNematodes;
  readonly selectedDiseases = this.questionnaire.selectedDiseases;
  readonly selectedSystem = this.questionnaire.selectedSystem;
  readonly field = this.data.field.cropField;
  private readonly currentSoilType = computed(
    () => this.soilTypes()?.find((soil) => soil.id === this.field.soilTypeId)?.name ?? null,
  );
  readonly currentSnapshot = computed<CropFieldSnapshot>(() => ({
    name: this.field.name,
    areaInHectares: this.field.areaInHectares,
    mainCropId: this.field.cropId ?? 0,
    agrifirmCropId: this.data.field.crop?.agrifirmCrop?.id ?? 0,
    mainCropName: this.data.field.crop?.agrifirmCrop?.name ?? '',
    soilTypeId: this.field.soilTypeId ?? 0,
    soilTypeName: this.currentSoilType() ?? '',
    startDate: this.field.startDate,
    endDate: this.field.endDate,
    isSkal: this.field.isSkal ?? null,
    isPlanetProof: this.field.isPlanetProof ?? false,
    province: this.field.province ?? null,
  }));
  readonly displayField = computed(() => this.questionnaire.snapshot() ?? this.currentSnapshot());
  readonly soilType = computed(() => this.displayField().soilTypeName || null);
  readonly snapshotReady = computed(
    () => this.field.soilTypeId === 0 || this.field.soilTypeId == null || this.soilTypesLoaded(),
  );
  readonly canSave = computed(
    () =>
      this.contextValid() &&
      this.questionnaire.ready() &&
      (this.questionnaire.snapshot() !== null || this.snapshotReady()),
  );
  get fieldConditions() {
    const field = this.questionnaire.snapshot() ?? this.field;
    return [
      { key: 'organic', registered: field.isSkal ?? null },
      { key: 'planetProof', registered: field.isPlanetProof ?? null },
    ] as const;
  }
  readonly activeStep = signal(1);
  private readonly stepDefinitions = computed(() => [
    {
      labelKey: 'greenManure.mainCropConditions',
      completed: this.selectedMainCrop() !== null && this.isNonInversionTillage() !== null,
    },
    { labelKey: 'greenManure.nematodes', completed: this.selectedNematodes() !== null },
    {
      labelKey: 'greenManure.diseases',
      completed: this.selectedDiseases() !== null,
    },
    {
      labelKey: 'greenManure.systemAndPriorities',
      completed: this.selectedSystem() !== null && this.prioritiesComplete(),
    },
    { labelKey: 'greenManure.review', completed: false },
  ]);
  private canEnterStep(step: number): boolean {
    const steps = this.stepDefinitions();
    return step >= 1 && step <= steps.length && steps.slice(1, step - 1).every((entry) => entry.completed);
  }
  readonly steps = computed(() =>
    this.stepDefinitions().map((step, index) => ({
      ...step,
      disabled: index + 1 > this.activeStep() && !this.canEnterStep(index + 1),
    })),
  );
  readonly canReview = computed(() => this.canEnterStep(this.stepDefinitions().length));
  readonly canAdvance = computed(() => this.canEnterStep(this.activeStep() + 1));
  readonly isComplete = computed(() =>
    this.steps()
      .slice(0, -1)
      .every((step) => step.completed),
  );

  constructor() {
    this.store.dispatch(loadSoilTypesInitiate());
    this.dialogRef.disableClose = true;
    merge(
      this.dialogRef.backdropClick(),
      this.dialogRef.keydownEvents().pipe(filter((event) => event.key === 'Escape')),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close());
    if (this.contextValid()) this.questionnaire.load(this.context);
  }

  retryLoad(): void {
    if (this.contextValid() && !this.questionnaire.hasUnsavedChanges()) this.questionnaire.load(this.context);
  }

  retrySoilTypes(): void {
    this.store.dispatch(loadSoilTypesInitiate());
  }

  saveAndAdvance(targetStep = this.activeStep() + 1): void {
    const step = this.activeStep();
    if (!this.canSave() || (step !== 1 && step !== 2 && step !== 3 && step !== 4)) return;
    if (!this.canAdvance()) return;
    this.save(() => {
      if (this.contextValid()) this.activeStep.set(targetStep);
    });
  }

  save(onSaved?: () => void): void {
    if (!this.canSave()) return;
    this.questionnaire.save(this.context, this.currentSnapshot(), onSaved, () =>
      this.snackBar.open(this.localeService.translate('greenManure.saveFailed'), undefined, { duration: 5000 }),
    );
  }

  refreshSnapshot(): void {
    if (!this.canSave() || !this.snapshotReady() || this.questionnaire.snapshot() === null || this.confirming) return;
    this.confirming = true;
    this.dialogs
      .confirm({ title: this.localeService.translate('greenManure.refreshConfirm'), pushHistoryState: false })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ status }) => {
        this.confirming = false;
        if (status === DIALOG_RETURN_TYPES.confirm && this.canSave())
          this.questionnaire.refreshSnapshot(this.context, this.currentSnapshot(), () =>
            this.snackBar.open(this.localeService.translate('greenManure.fieldDataRefreshed'), undefined, {
              duration: 3000,
            }),
          );
      });
  }

  close(): void {
    if (this.questionnaire.operation() !== null || this.confirming) return;
    if (!this.questionnaire.hasUnsavedChanges()) {
      this.dialogRef.close();
      return;
    }
    this.confirming = true;
    this.dialogs
      .pendingChanges()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ status }) => {
        this.confirming = false;
        if (status === DIALOG_RETURN_TYPES.confirm) this.dialogRef.close();
      });
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (this.questionnaire.hasUnsavedChanges() || this.questionnaire.operation() !== null) {
      event.preventDefault();
    }
  }

  goToStep(step: number): void {
    if (
      this.questionnaire.loadFailed() ||
      this.questionnaire.operation() !== null ||
      step < 1 ||
      step > this.steps().length
    )
      return;
    if (step > this.activeStep()) {
      if (this.canEnterStep(step)) this.saveAndAdvance(step);
    } else if (step < this.activeStep()) {
      this.save(() => {
        if (this.contextValid()) this.activeStep.set(step);
      });
    }
  }

  setConcern<T extends string>(
    selection: Signal<T[] | null> & { update: (updater: (current: T[] | null) => T[] | null) => void },
    value: NoInfer<T> | null,
    checked: boolean,
  ): void {
    selection.update((current) => {
      if (value === null) return checked ? [] : null;
      const next = (current ?? []).filter((entry) => entry !== value);
      if (checked) return [...next, value];
      return next.length ? next : null;
    });
  }
}
